"use client";

import { usePathname } from "next/navigation";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { useSession } from "@/app/hooks/useSession";

interface AiUsageState {
  usage: number;
  limit: number;
  remaining: number;
  loaded: boolean;
  increment: () => void;
  set: (usage: number, limit: number) => void;
  refetch: () => void;
}

const AiUsageContext = createContext<AiUsageState | null>(null);

export function AiUsageProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<{ usage: number; limit: number } | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [fetchTrigger, setFetchTrigger] = useState(0);
  const pathname = usePathname();
  const { isAuthenticated, isLoading: sessionLoading } = useSession();

  useEffect(() => {
    if (sessionLoading) return;
    if (!isAuthenticated) {
      setLoaded(true);
      return;
    }
    fetch("/api/ai-usage")
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data) setState({ usage: data.usage, limit: data.limit });
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [pathname, isAuthenticated, sessionLoading, fetchTrigger]);

  const increment = useCallback(() => {
    setState((prev) => (prev ? { ...prev, usage: prev.usage + 1 } : prev));
  }, []);

  const set = useCallback((usage: number, limit: number) => {
    setState({ usage, limit });
  }, []);

  const refetch = useCallback(() => {
    setFetchTrigger((c) => c + 1);
  }, []);

  const value: AiUsageState = useMemo(
    () => ({
      usage: state?.usage ?? 0,
      limit: state?.limit ?? 0,
      remaining: state ? Math.max(0, state.limit - state.usage) : 0,
      loaded,
      increment,
      set,
      refetch,
    }),
    [state, loaded, increment, set, refetch]
  );

  return <AiUsageContext.Provider value={value}>{children}</AiUsageContext.Provider>;
}

export function useAiUsage() {
  const context = useContext(AiUsageContext);
  if (!context) {
    throw new Error("useAiUsage must be used within AiUsageProvider");
  }
  return context;
}
